import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Logo from './Logo';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { useWorkspace } from '../contexts/WorkspaceContext';
import {
  ArrowLeft,
  Palette,
  Users,
  Globe,
  Shield,
  Rss,
  Megaphone,
  Plug,
  KeyRound,
  CreditCard,
  Download,
  Lock,
  HelpCircle,
} from 'lucide-react';

const NAV_SECTIONS = [
  {
    label: 'Workspace',
    items: [
      { key: 'branding', label: 'Branding',         path: '/settings/branding',        icon: Palette },
      { key: 'team',     label: 'Team',             path: '/settings/team',            icon: Users,   locked: true },
      { key: 'domain',   label: 'Domain',           path: '/settings/domain',          icon: Globe,   locked: true },
      { key: 'privacy',  label: 'Privacy & Access', path: '/settings/privacy',         icon: Shield },
    ],
  },
  {
    label: 'Product',
    items: [
      { key: 'sources',   label: 'Signal Sources', path: '/sources',              icon: Rss },
      { key: 'changelog', label: 'Changelog',      path: '/changelog',            icon: Megaphone },
    ],
  },
  {
    label: 'Connections',
    items: [
      { key: 'integrations', label: 'Integrations', path: '/settings/integrations', icon: Plug },
      { key: 'api-tokens',   label: 'API Tokens',   path: '/settings/api-tokens',   icon: KeyRound },
    ],
  },
  {
    label: 'Billing & Data',
    items: [
      { key: 'billing', label: 'Billing',     path: '/settings/billing', icon: CreditCard, locked: true },
      { key: 'data',    label: 'Data export', path: '/settings/data',    icon: Download },
    ],
  },
];

const navItemBase =
  'group flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40';

const SettingsSidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { currentWorkspace } = useWorkspace();

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(`${path}/`);

  return (
    <aside
      className="hidden lg:flex lg:flex-col lg:w-[240px] lg:flex-shrink-0 h-screen sticky top-0 border-r bg-card"
      data-testid="settings-sidebar"
    >
      {/* Header — workspace + back link */}
      <div className="p-3 border-b">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-lg">{currentWorkspace?.icon || '📦'}</span>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold truncate">
              {currentWorkspace?.name || 'Workspace'}
            </div>
          </div>
        </div>
        <button
          type="button"
          onClick={() => navigate('/app')}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
          data-testid="settings-back-to-dashboard"
        >
          <ArrowLeft className="h-3 w-3" />
          Back to dashboard
        </button>
      </div>

      <ScrollArea className="flex-1">
        <div className="px-2 py-2">
          {NAV_SECTIONS.map((section) => (
            <div key={section.label} className="mb-3">
              <div className="px-2.5 pt-2 pb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                {section.label}
              </div>
              <div className="space-y-0.5">
                {section.items.map((item) => {
                  const Icon = item.icon;
                  const active = isActive(item.path);
                  return (
                    <button
                      key={item.key}
                      type="button"
                      onClick={() => navigate(item.path)}
                      className={`${navItemBase} w-full ${
                        active
                          ? 'bg-primary/10 text-primary font-medium'
                          : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                      }`}
                      data-testid={`settings-nav-${item.key}`}
                    >
                      <Icon className={`h-4 w-4 flex-shrink-0 ${active ? 'text-primary' : ''}`} />
                      <span className="flex-1 text-left truncate">{item.label}</span>
                      {item.locked && (
                        <Lock className="h-3 w-3 text-muted-foreground flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </ScrollArea>

      <div className="border-t p-2">
        <button
          type="button"
          onClick={() => navigate('/faq')}
          className={`${navItemBase} w-full text-muted-foreground hover:bg-muted hover:text-foreground`}
          data-testid="settings-nav-help"
        >
          <HelpCircle className="h-4 w-4" />
          <span className="flex-1 text-left">Help</span>
        </button>
      </div>
    </aside>
  );
};

/**
 * SettingsLayout — wraps every /settings/* sub-page with the SettingsSidebar +
 * a consistent content shell. Children render inside the main column.
 */
export const SettingsLayout = ({ title, description, children }) => {
  return (
    <div className="min-h-screen bg-background flex">
      <SettingsSidebar />
      <main className="flex-1 min-w-0">
        <div className="container mx-auto max-w-3xl px-4 sm:px-8 py-8" data-testid="settings-main">
          {(title || description) && (
            <header className="mb-8">
              {title && <h1 className="text-2xl font-bold tracking-tight mb-1" data-testid="settings-page-title">{title}</h1>}
              {description && <p className="text-sm text-muted-foreground">{description}</p>}
            </header>
          )}
          {children}
        </div>
      </main>
    </div>
  );
};

export default SettingsLayout;
